// A list grid's Quick Filters (Pattern 180): the shared chip row (QuickFilterBar.tsx) on the grid's
// status filter (or the Default field the grid names instead, `StatusFilterConfig.field`: Due Date
// Control's Department) — the same item the Filters panel's Default Filters section sets (filterPanel.tsx
// withStatusFilter), so the two always agree. Requires the Pro 6.9 grid context (a toolbar slot).
//
// Shown where the grid has status options and the viewer has Show Quick Filters on (the switch in
// the panel; useShowQuickFilters, per viewer). Consumers: the list toolbar (AeronetDataGrid
// `statusFilterOptions`, the Tools list) and Inventory Demand's toolbars, whose row also holds
// Group By (`inline`). A chip edit goes through the grid's filter API with MUI's own reason, so a
// list grid stages it for Display → Save like any panel edit; nothing here writes.
import React from "react";
import { gridFilterModelSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid-pro";

import { QuickFilterBar, QuickFilterGroup, useShowQuickFilters } from "../QuickFilterBar";
import { statusFilterValues, withStatusFilter, type StatusFilterConfig } from "./filterPanel";

export interface StatusQuickFiltersProps {
  statusFilter?: StatusFilterConfig;
  /** Label and chips only, for a row that holds other controls (Demand's Group By). */
  inline?: boolean;
}

export function StatusQuickFilters({ statusFilter, inline = false }: StatusQuickFiltersProps) {
  const apiRef = useGridApiContext();
  const model = useGridSelector(apiRef, gridFilterModelSelector);
  const [show] = useShowQuickFilters(
    statusFilter?.quickFiltersKey ?? "quick-filters",
    statusFilter?.quickFiltersDefault ?? true
  );
  const options = (statusFilter?.options ?? []).filter((o) => o.value != null && String(o.value) !== "");
  if (!statusFilter || !options.length || !show) return null;
  const chips = {
    options: options.map((o) => ({ value: String(o.value), label: o.label })),
    selected: statusFilterValues(model, statusFilter.field),
    multiple: Boolean(statusFilter.multiple),
    exhaustive: false,
    onChange: (values: string[]) => {
      const next = withStatusFilter(model, values, statusFilter);
      apiRef.current.setFilterModel(next.model as any, next.reason);
    },
  };
  return inline ? <QuickFilterGroup {...chips} /> : <QuickFilterBar testId="status-quick-filters" {...chips} />;
}

export default StatusQuickFilters;
